import React, { useState, useEffect } from 'react';
import { fetchLivePricing } from '../../lib/pricing';
import { todayToronto } from '../../lib/time';

const CustomerEditModal = ({ customer, onSave, onClose }) => {
    const [activeTab, setActiveTab] = useState('details');
    const [formData, setFormData] = useState({
        first_name: customer.first_name || '',
        last_name: customer.last_name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        // Address fields are flat in API response (getEnhancedCustomers returns them at root level)
        street: customer.street || '',
        city: customer.city || '',
        postal_code: customer.postal_code || '',
        address_type: customer.property_type || '',
        delivery_instructions: customer.delivery_instruction || '',
        // Plan fields are flat in API response (not nested under customer.plan)
        meal_plan_type: customer.meal_plan_type || '',
        meal_plan_term: customer.meal_plan_term || '',
        plan_term: customer.plan_term || '',
        nv_preference: customer.nv_preference || '',
        add_ons: customer.add_ons || [],
        // Pricing/billing fields are flat in API response (not nested under customer.billing)
        additional_charges: customer.additional_charges || 0,
        additional_discount: customer.additional_discount || 0,
        plan_renewal_date: customer.plan_renewal_date || '',
        plan_end_date: customer.plan_end_date || '',
        plan_start_date: customer.plan_start_date || '', // Used for renewal calculation
    });

    const [pricing, setPricing] = useState(null);

    // FIX: Auto-set nv_preference to "-" for "5 Days Veg" plans
    React.useEffect(() => {
        if (formData.meal_plan_term?.includes('5 Days Veg')) {
            setFormData(prev => ({
                ...prev,
                nv_preference: '-'
            }));
        }
    }, [formData.meal_plan_term]);
    const [loading, setLoading] = useState(true);
    const [selectedAddOns, setSelectedAddOns] = useState([]);

    // Fetch live pricing on mount
    useEffect(() => {
        const loadPricing = async () => {
            try {
                const livePricing = await fetchLivePricing();
                setPricing(livePricing);

                // Parse existing add-ons — handle both [{name,price}] objects and plain strings
                if (customer.add_ons) {
                    const addOnsArray = Array.isArray(customer.add_ons) ? customer.add_ons : String(customer.add_ons).split(',');
                    const existingAddOns = addOnsArray.map(a => {
                        if (typeof a === 'object' && a !== null) return (a.name || '').trim();
                        return String(a).trim();
                    }).filter(a => a);
                    setSelectedAddOns(existingAddOns);
                }
            } catch (error) {
                console.error('Failed to load pricing:', error);
            } finally {
                setLoading(false);
            }
        };
        loadPricing();
    }, [customer.add_ons]);

    // Auto-calculate renewal date when plan start date or billing term changes
    useEffect(() => {
        if (formData.plan_start_date && formData.plan_term) {
            // Count N Mon-Fri service days from start (inclusive), return the Nth day
            const addServiceDays = (start, n) => {
                let current = new Date(start);
                let counted = 0;
                while (counted < n) {
                    const dow = current.getUTCDay(); // 0=Sun,6=Sat
                    if (dow >= 1 && dow <= 5) {
                        counted++;
                        if (counted === n) break;
                    }
                    current = new Date(current.getTime() + 86400000);
                }
                return current;
            };

            const calculateDates = (startDate, billingTerm) => {
                // Use noon UTC to avoid DST/timezone boundary issues
                const start = new Date(startDate + 'T12:00:00Z');
                if (isNaN(start.getTime())) return null;

                let endDate;
                const term = billingTerm.toLowerCase();

                if (term === 'weekly') {
                    // 5 Mon-Fri service days from start inclusive
                    endDate = addServiceDays(start, 5);
                } else if (term === 'monthly') {
                    // start + 1 month - 1 day (matches backend pauseUtils.calculateEndDate)
                    const y = start.getUTCFullYear(), m = start.getUTCMonth(), d = start.getUTCDate();
                    const oneMonthLater = new Date(Date.UTC(y, m + 1, d));
                    endDate = new Date(oneMonthLater.getTime() - 86400000);
                } else {
                    return null;
                }

                // renewalDeadline = endDate + 1 calendar day
                const renewal = new Date(endDate.getTime() + 86400000);
                return {
                    endStr: endDate.toISOString().split('T')[0],
                    renewalStr: renewal.toISOString().split('T')[0],
                };
            };

            const dates = calculateDates(formData.plan_start_date, formData.plan_term);
            if (dates) {
                setFormData(prev => ({
                    ...prev,
                    plan_renewal_date: dates.renewalStr,
                    plan_end_date: dates.endStr
                }));
            }
        }
    }, [formData.plan_start_date, formData.plan_term]);

    // Get available meal plan types
    const getMealPlanTypes = () => {
        if (!pricing) return [];
        return Object.keys(pricing.plans || {});
    };

    // Get available meal plan terms based on selected type
    const getMealPlanTermOptions = () => {
        if (!pricing || !formData.meal_plan_type) return [];

        const planData = pricing.plans[formData.meal_plan_type];
        if (!planData) return [];

        // Extract unique terms from plan keys (format: "5 Days Veg" or "5 Days Non-Veg|1 NV/day")
        const terms = Object.keys(planData).map(key => key.split('|')[0]);
        return [...new Set(terms)]; // Remove duplicates
    };

    // Get available NV preferences based on selected term
    const getNVPreferenceOptions = () => {
        if (!pricing || !formData.meal_plan_type || !formData.meal_plan_term) return [];

        const planData = pricing.plans[formData.meal_plan_type];
        if (!planData) return [];

        // Find all keys that match the current term
        const matchingKeys = Object.keys(planData).filter(key => key.startsWith(formData.meal_plan_term));

        // Extract NV preferences
        const preferences = matchingKeys.map(key => {
            const parts = key.split('|');
            return parts.length > 1 ? parts[1] : '-';
        });

        // Return unique preferences with labels
        const uniquePrefs = [...new Set(preferences)];
        return uniquePrefs.map(pref => ({
            value: pref,
            label: pref === '-' ? 'Veg Only' : pref
        }));
    };

    // Get available trucker types
    const getTruckerTypes = () => {
        if (!pricing) return [];
        return Object.keys(pricing.truckers || {});
    };

    // Get available trucker terms for selected type
    const getTruckerTermOptions = () => {
        if (!pricing || !formData.meal_plan_type) return [];
        const truckerData = pricing.truckers[formData.meal_plan_type];
        if (!truckerData) return [];
        return Object.keys(truckerData);
    };

    // Dynamic pricing calculation using live pricing
    const calculateTotal = () => {
        if (!pricing) return { planPrice: 0, addonPrice: 0, subtotal: 0, total: 0 };

        let planPrice = 0;
        let addonPrice = 0;

        // Calculate plan price
        if (formData.meal_plan_type && formData.meal_plan_term && formData.plan_term) {
            if (formData.meal_plan_type.includes('Truckers')) {
                // Truckers pricing
                const truckerData = pricing.truckers[formData.meal_plan_type];
                if (truckerData && formData.meal_plan_term) {
                    planPrice = truckerData[formData.meal_plan_term] || 0;
                }
            } else {
                // Regular meal plans
                const planData = pricing.plans[formData.meal_plan_type];
                if (planData) {
                    // Build key: "5 Days Veg" or "5 Days Non-Veg|1 NV/day"
                    const nvPref = formData.nv_preference && formData.nv_preference !== '-' ? formData.nv_preference : '';
                    const planKey = nvPref ? `${formData.meal_plan_term}|${nvPref}` : formData.meal_plan_term;

                    const termPricing = planData[planKey];
                    if (termPricing) {
                        planPrice = termPricing[formData.plan_term] || 0;
                    }
                }
            }
        }

        // Calculate add-ons price
        if (selectedAddOns.length > 0 && formData.plan_term && pricing.addons) {
            addonPrice = selectedAddOns.reduce((sum, addon) => {
                const addonData = pricing.addons[addon];
                return sum + (addonData?.[formData.plan_term] || 0);
            }, 0);
        }

        const subtotal = planPrice + addonPrice;
        const withCharges = subtotal + (parseFloat(formData.additional_charges) || 0);
        const total = withCharges - (parseFloat(formData.additional_discount) || 0);

        return {
            planPrice,
            addonPrice,
            subtotal,
            total: Math.max(0, total)
        };
    };

    const totalPricing = calculateTotal();

    const handleSave = (postAction = null) => {
        // Confirmation before Save & Renew (records income, cannot be undone)
        if (postAction === 'renew') {
            const confirmed = window.confirm(
                `Are you sure you want to Save & Renew?\n\n` +
                `This will:\n` +
                `- Record $${totalPricing.total.toFixed(2)} as income\n` +
                `- Mark customer as Paid\n` +
                `- Reset plan dates\n\n` +
                `This action cannot be undone.`
            );
            if (!confirmed) return;
        }

        // Warn if activating with a past start date
        if (postAction === 'activate') {
            const effectiveStartDate = formData.plan_start_date || customer.plan_start_date;
            const today = todayToronto();
            if (effectiveStartDate && effectiveStartDate < today) {
                const confirmed = window.confirm(
                    `Warning: The plan start date (${effectiveStartDate}) is in the past.\n\n` +
                    `Today is ${today}.\n\n` +
                    `Do you still want to activate with expired dates?\n` +
                    `Click Cancel to go back and update the dates first.`
                );
                if (!confirmed) return;
            }
        }

        const updates = {};

        // Handle basic fields
        if (formData.first_name !== customer.first_name) updates.first_name = formData.first_name;
        if (formData.last_name !== customer.last_name) updates.last_name = formData.last_name;
        if (formData.email !== customer.email) updates.email = formData.email;
        if (formData.phone !== customer.phone) updates.phone = formData.phone;

        // Handle address fields - nest them in address object for backend
        const addressUpdates = {};
        if (formData.street !== (customer.street || '')) addressUpdates.street = formData.street;
        if (formData.city !== (customer.city || '')) addressUpdates.city = formData.city;
        if (formData.postal_code !== (customer.postal_code || '')) addressUpdates.postal_code = formData.postal_code;
        if (formData.address_type !== (customer.property_type || '')) addressUpdates.property_type = formData.address_type;
        if (formData.delivery_instructions !== (customer.delivery_instruction || '')) {
            addressUpdates.delivery_instructions = formData.delivery_instructions;
        }

        if (Object.keys(addressUpdates).length > 0) {
            updates.address = addressUpdates;
        }

        // Handle plan fields — only send when changed
        if (formData.meal_plan_type !== customer.meal_plan_type) updates.meal_plan_type = formData.meal_plan_type;
        if (formData.meal_plan_term !== customer.meal_plan_term) updates.meal_plan_term = formData.meal_plan_term;

        // FIX: Map NV Preference label back to backend value
        // Frontend shows "Veg Only" but backend expects "-"
        if (formData.nv_preference !== customer.nv_preference) {
            const nvPreferenceValue = formData.nv_preference === 'Veg Only' ? '-' : formData.nv_preference;
            updates.nv_preference = nvPreferenceValue;
        }

        // Handle add-ons - always send as {name, price} objects so DB stores real prices
        const originalAddOns = Array.isArray(customer.add_ons)
            ? customer.add_ons.map(a => typeof a === 'object' ? (a.name || '') : String(a)).filter(a => a)
            : (customer.add_ons ? String(customer.add_ons).split(',').map(a => a.trim()).filter(a => a) : []);

        const originalAddOnsSorted = JSON.stringify([...originalAddOns].sort());
        const selectedAddOnsSorted = JSON.stringify([...selectedAddOns].sort());

        if (formData.plan_term !== customer.plan_term) updates.plan_term = formData.plan_term;

        // Only send pricing when a plan-related field actually changed
        const planFieldsChanged = formData.meal_plan_type !== customer.meal_plan_type
            || formData.meal_plan_term !== customer.meal_plan_term
            || formData.nv_preference  !== customer.nv_preference
            || formData.plan_term      !== customer.plan_term
            || selectedAddOnsSorted    !== originalAddOnsSorted
            || formData.additional_charges !== customer.additional_charges
            || formData.additional_discount !== customer.additional_discount;

        // Send add-ons with live prices whenever plan fields changed OR selection changed
        // This also fixes existing records that may have been saved with price: 0
        if (planFieldsChanged || selectedAddOnsSorted !== originalAddOnsSorted) {
            updates.add_ons = selectedAddOns.map(addonName => ({
                name: addonName,
                price: pricing?.addons?.[addonName]?.[formData.plan_term] || 0
            }));
        }

        if (planFieldsChanged) {
            updates.plan_price  = totalPricing.planPrice;
            updates.total_price = totalPricing.total;
        }

        // Send plan_start_date if it changed
        if (formData.plan_start_date && formData.plan_start_date !== customer.plan_start_date) {
            updates.plan_start_date = formData.plan_start_date;
        }

        if (formData.plan_renewal_date !== customer.plan_renewal_date) {
            updates.plan_renewal_date = formData.plan_renewal_date;
        }
        if (formData.plan_end_date !== customer.plan_end_date) {
            updates.plan_end_date = formData.plan_end_date;
        }
        if (formData.additional_charges !== customer.additional_charges) {
            updates.additional_charges = parseFloat(formData.additional_charges) || 0;
        }
        if (formData.additional_discount !== customer.additional_discount) {
            updates.additional_discount = parseFloat(formData.additional_discount) || 0;
        }

        if (Object.keys(updates).length === 0 && !postAction) {
            alert('No changes detected');
            onClose();
            return;
        }

        onSave(updates, postAction);
    };

    const mealPlanTypes = getMealPlanTypes();
    const mealPlanTermOptions = getMealPlanTermOptions();
    const nvPreferenceOptions = getNVPreferenceOptions();
    const truckerTypes = getTruckerTypes();
    const truckerTermOptions = getTruckerTermOptions();
    const isTruckerPlan = formData.meal_plan_type?.includes('Truckers');

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6">
                    <p>Loading pricing data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
                <h3 className="text-lg font-semibold mb-4">Edit Customer: {customer.first_name} {customer.last_name}</h3>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 mb-4">
                    <button
                        className={`py-2 px-4 text-sm font-medium ${activeTab === 'details' ? 'border-b-2 border-orange-500 text-orange-600' : 'text-gray-500 hover:text-gray-700'}`}
                        onClick={() => setActiveTab('details')}
                    >
                        Details & Plan
                    </button>
                </div>

                {activeTab === 'details' && (
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">First Name</label>
                            <input
                                type="text"
                                value={formData.first_name}
                                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Last Name</label>
                            <input
                                type="text"
                                value={formData.last_name}
                                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Email</label>
                            <input
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            />
                            <p className="text-xs text-gray-500 mt-1">⚠️ Changing email will update the customer's login credentials</p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Phone</label>
                            <input
                                type="text"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Street Address</label>
                            <input
                                type="text"
                                value={formData.street}
                                onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">City</label>
                            <input
                                type="text"
                                value={formData.city}
                                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Postal Code</label>
                            <input
                                type="text"
                                value={formData.postal_code}
                                onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Property Type</label>
                            <select
                                value={formData.address_type}
                                onChange={(e) => setFormData({ ...formData, address_type: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            >
                                <option value="">Select...</option>
                                <option value="House">House</option>
                                <option value="House – Basement">House – Basement</option>
                                <option value="House – Front Door">House – Front Door</option>
                                <option value="Apartment">Apartment</option>
                                <option value="Condo">Condo</option>
                                <option value="Townhouse">Townhouse</option>
                                <option value="Business">Business</option>
                                <option value="Office">Office</option>
                            </select>
                        </div>

                        <div className="col-span-2">
                            <label className="block text-sm font-medium mb-1">Delivery Instructions / Buzz Code</label>
                            <textarea
                                value={formData.delivery_instructions}
                                onChange={(e) => setFormData({ ...formData, delivery_instructions: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                                rows={2}
                                placeholder="e.g., Leave at side door, ring bell twice, buzz code: 1234"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Meal Plan Type</label>
                            <select
                                value={formData.meal_plan_type}
                                onChange={(e) => {
                                    const newType = e.target.value;
                                    setFormData({
                                        ...formData,
                                        meal_plan_type: newType,
                                        meal_plan_term: '', // Reset dependent fields
                                        nv_preference: ''
                                    });
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            >
                                <option value="">Select...</option>
                                {mealPlanTypes.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                                {truckerTypes.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">
                                {isTruckerPlan ? 'Trucker Plan Term' : 'Meal Plan Term'}
                            </label>
                            <select
                                value={formData.meal_plan_term}
                                onChange={(e) => {
                                    setFormData({
                                        ...formData,
                                        meal_plan_term: e.target.value,
                                        nv_preference: '' // Reset NV preference when term changes
                                    });
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                                disabled={!formData.meal_plan_type}
                            >
                                <option value="">Select...</option>
                                {isTruckerPlan ? (
                                    truckerTermOptions.map(option => (
                                        <option key={option} value={option}>{option}</option>
                                    ))
                                ) : (
                                    mealPlanTermOptions.map(option => (
                                        <option key={option} value={option}>{option}</option>
                                    ))
                                )}
                            </select>
                            {!formData.meal_plan_type && (
                                <p className="text-xs text-gray-500 mt-1">Select Meal Plan Type first</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Plan Term (Billing)</label>
                            <select
                                value={formData.plan_term}
                                onChange={(e) => setFormData({ ...formData, plan_term: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            >
                                <option value="">Select...</option>
                                {isTruckerPlan ? (
                                    <option value="Weekly">Weekly</option>
                                ) : (
                                    <>
                                        <option value="Weekly">Weekly</option>
                                        <option value="Monthly">Monthly</option>
                                    </>
                                )}
                            </select>
                        </div>

                        {!isTruckerPlan && !formData.meal_plan_term?.includes('5 Days Veg') && (
                            <div>
                                <label className="block text-sm font-medium mb-1">NV Preference</label>
                                <select
                                    value={formData.nv_preference}
                                    onChange={(e) => setFormData({ ...formData, nv_preference: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                                    disabled={!formData.meal_plan_term}
                                >
                                    <option value="">Select...</option>
                                    {nvPreferenceOptions.map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                                {!formData.meal_plan_term && (
                                    <p className="text-xs text-gray-500 mt-1">Select Meal Plan Term first</p>
                                )}
                            </div>
                        )}

                        <div className="col-span-2">
                            <label className="block text-sm font-medium mb-2">Add-ons (Select Multiple)</label>
                            <div className="grid grid-cols-2 gap-2 border border-gray-300 rounded p-3 bg-gray-50 max-h-32 overflow-y-auto">
                                {pricing && pricing.addons && Object.keys(pricing.addons).map(addon => (
                                    <label key={addon} className="flex items-center space-x-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={selectedAddOns.includes(addon)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedAddOns([...selectedAddOns, addon]);
                                                } else {
                                                    setSelectedAddOns(selectedAddOns.filter(a => a !== addon));
                                                }
                                            }}
                                            className="rounded border-gray-300"
                                        />
                                        <span className="text-sm">{addon}</span>
                                    </label>
                                ))}
                            </div>
                            {selectedAddOns.length > 0 && (
                                <p className="text-xs text-gray-600 mt-1">
                                    Selected: {selectedAddOns.join(', ')}
                                </p>
                            )}
                        </div>

                        {/* Plan Start Date */}
                        <div>
                            <label className="block text-sm font-medium mb-1">Plan Start Date</label>
                            <input
                                type="date"
                                value={formData.plan_start_date}
                                onChange={(e) => {
                                    const selectedDate = e.target.value;
                                    if (selectedDate) {
                                        const day = new Date(selectedDate).getUTCDay();
                                        if (day === 0 || day === 6) {
                                            alert('Please select a weekday (Monday-Friday) for the Plan Start Date. Delivery is not available on Saturday and Sunday.');
                                            return;
                                        }
                                    }
                                    setFormData({ ...formData, plan_start_date: selectedDate });
                                }}
                                className={`w-full px-3 py-2 border rounded text-sm bg-white ${formData.plan_start_date && (new Date(formData.plan_start_date).getUTCDay() === 0 || new Date(formData.plan_start_date).getUTCDay() === 6)
                                    ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                    }`}
                            />
                            {(formData.plan_start_date && (new Date(formData.plan_start_date).getUTCDay() === 0 || new Date(formData.plan_start_date).getUTCDay() === 6)) ? (
                                <p className="text-[10px] text-red-600 mt-1">Weekend selected. Please choose a weekday.</p>
                            ) : (
                                <p className="text-xs text-gray-500 mt-1">
                                    End date and renewal date will be auto-calculated when start date changes.
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Plan Renewal Date {formData.plan_start_date && <span className="text-blue-600">(Auto-calculated)</span>}
                            </label>
                            <input
                                type="date"
                                value={formData.plan_renewal_date}
                                onChange={(e) => setFormData({ ...formData, plan_renewal_date: e.target.value })}
                                className={`w-full px-3 py-2 border rounded text-sm ${formData.plan_start_date ? 'border-blue-300 bg-blue-50' : 'border-gray-300'
                                    }`}
                                readOnly={!!formData.plan_start_date}
                            />
                            {formData.plan_start_date ? (
                                <p className="text-xs text-blue-600 mt-1">
                                    ✓ Auto-calculated based on start date and term ({formData.plan_term})
                                </p>
                            ) : (
                                <p className="text-xs text-orange-600 mt-1">
                                    ⚠️ Admin override - this will be the final renewal date
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Plan End Date {formData.plan_start_date && <span className="text-blue-600">(Auto-calculated)</span>}
                            </label>
                            <input
                                type="date"
                                value={formData.plan_end_date}
                                onChange={(e) => setFormData({ ...formData, plan_end_date: e.target.value })}
                                className={`w-full px-3 py-2 border rounded text-sm ${formData.plan_start_date ? 'border-blue-300 bg-blue-50' : 'border-gray-300'
                                    }`}
                                readOnly={!!formData.plan_start_date}
                            />
                            {formData.plan_start_date && (
                                <p className="text-xs text-blue-600 mt-1">
                                    ✓ End of cycle (one day before renewal)
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Additional Charges ($)</label>
                            <input
                                type="number"
                                step="0.01"
                                value={formData.additional_charges}
                                onChange={(e) => setFormData({ ...formData, additional_charges: parseFloat(e.target.value) || 0 })}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Additional Discount ($)</label>
                            <input
                                type="number"
                                step="0.01"
                                value={formData.additional_discount}
                                onChange={(e) => setFormData({ ...formData, additional_discount: parseFloat(e.target.value) || 0 })}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                            />
                        </div>
                    </div>
                )}

                {/* Pricing Summary - Only visible in details tab */}
                {activeTab === 'details' && (
                    <div className="mt-6 p-4 bg-white rounded-lg">
                        <h4 className="font-medium mb-3">Pricing Summary</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <div className="flex justify-between">
                                    <span>Plan Price:</span>
                                    <span>${totalPricing.planPrice.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Add-ons:</span>
                                    <span>${totalPricing.addonPrice.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Additional Charges:</span>
                                    <span>${(parseFloat(formData.additional_charges) || 0).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Additional Discount:</span>
                                    <span>-${(parseFloat(formData.additional_discount) || 0).toFixed(2)}</span>
                                </div>
                            </div>
                            <div className="border-l pl-4">
                                <div className="flex justify-between text-lg font-semibold text-orange-500">
                                    <span>Total Amount:</span>
                                    <span>${totalPricing.total.toFixed(2)}</span>
                                </div>
                                {!pricing && (
                                    <p className="text-xs text-orange-600 mt-2">⚠️ Using fallback pricing (failed to load live pricing)</p>
                                )}
                                <div className="text-xs text-gray-700 mt-1">
                                    *Prices include HST
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex justify-end space-x-3 mt-6">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50 font-medium"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => handleSave()}
                        className="px-4 py-2 bg-orange-500 text-white rounded text-sm hover:bg-orange-600 font-medium shadow-sm"
                    >
                        {loading ? 'Saving...' : 'Save'}
                    </button>
                    <button
                        onClick={() => handleSave('activate')}
                        className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 font-medium shadow-sm"
                    >
                        {loading ? 'Processing...' : 'Save & Activate'}
                    </button>
                    <button
                        onClick={() => handleSave('renew')}
                        disabled={!formData.meal_plan_type || !formData.meal_plan_term || !formData.plan_term || !formData.nv_preference}
                        className={`px-4 py-2 rounded text-sm font-bold shadow-sm ${
                            !formData.meal_plan_type || !formData.meal_plan_term || !formData.plan_term || !formData.nv_preference
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                        title={!formData.meal_plan_type || !formData.meal_plan_term || !formData.plan_term || !formData.nv_preference
                            ? 'Please select Meal Plan Type, Meal Plan Term, Plan Term, and NV Preference first'
                            : ''}
                    >
                        {loading ? 'Processing...' : 'Save & Renew (Paid)'}
                    </button>
                </div>
            </div>
        </div>
    );
};


export default CustomerEditModal;
